import { SubscriptionPlan } from "@/services/api_types";

export type FrontendPlan = SubscriptionPlan & {
  title: string;
  price: string;
  suffix: string;
  detail: string;
  badge?: string;
  isLifetime: boolean;
};

// UI overlay per plan: copy + visual treatment that the backend doesn't know
// about. Commercial fields (amount, interval_months, display_name) act as
// fallback defaults for the first paint before /api/subscription/plans
// resolves; the merge below lets the live backend values override them.
type PlanOverlay = SubscriptionPlan & {
  title: string;
  detail: string;
  badge?: string;
  isLifetime: boolean;
};

const SELECTABLE_PLAN_OVERLAYS: PlanOverlay[] = [
  {
    plan_id: "monthly",
    amount: "11.00",
    interval_months: 1,
    display_name: "Maandelijks",
    title: "Maandabonnement",
    detail: "Maandelijkse afschrijving na de proefweek",
    isLifetime: false
  },
  {
    plan_id: "yearly",
    amount: "84.00",
    interval_months: 12,
    display_name: "Jaarlijks",
    title: "Jaarabonnement",
    detail: "Jaarlijkse afschrijving na de proefweek",
    badge: "Meest gekozen",
    isLifetime: false
  },
  {
    plan_id: "lifetime",
    amount: "200.00",
    interval_months: 0,
    display_name: "De hele schooltijd",
    title: "De hele schooltijd",
    detail: "Eenmalig betalen na de proefweek",
    isLifetime: true
  }
];

const LEGACY_PLAN_OVERLAYS: PlanOverlay[] = [
  {
    plan_id: "yearly_second_child",
    amount: "60.00",
    interval_months: 12,
    display_name: "Jaarlijks tweede kind",
    title: "Jaarabonnement tweede kind",
    detail: "Jaarlijkse afschrijving na de proefweek",
    isLifetime: false
  },
  {
    plan_id: "monthly_legacy",
    amount: "6.99",
    interval_months: 1,
    display_name: "Maandelijks",
    title: "Maandabonnement",
    detail: "Maandelijkse afschrijving",
    isLifetime: false
  }
];

const PLAN_LOOKUP_OVERLAYS = [
  ...SELECTABLE_PLAN_OVERLAYS,
  ...LEGACY_PLAN_OVERLAYS
];

export const DEFAULT_CHECKOUT_PLAN_ID = "monthly";

// Headline price/suffix derived from the merged commercial fields so a
// backend price change propagates without a frontend release. Yearly is
// rendered as a per-month equivalent (e.g. €84/yr → "€7,00 /maand") to make
// the comparison with the monthly plan obvious on plan-selection cards.
function withDerivedDisplay(overlay: PlanOverlay): FrontendPlan {
  const amountStr = overlay.amount.replace(".", ",");
  if (overlay.isLifetime) {
    return { ...overlay, price: `€${amountStr}`, suffix: "eenmalig" };
  }
  if (overlay.interval_months === 12) {
    const perMonth = (parseFloat(overlay.amount) / overlay.interval_months)
      .toFixed(2)
      .replace(".", ",");
    return { ...overlay, price: `€${perMonth}`, suffix: "/maand" };
  }
  return { ...overlay, price: `€${amountStr}`, suffix: "/maand" };
}

// AppContext seeds its `subscriptionPlans` state with this so the first paint
// has real numbers; /api/subscription/plans then overrides via getFrontendPlans.
export const FRONTEND_SUBSCRIPTION_PLANS: FrontendPlan[] =
  SELECTABLE_PLAN_OVERLAYS.filter((plan) => !plan.isLifetime).map(withDerivedDisplay);

export function getFrontendPlans(apiPlans: SubscriptionPlan[]): FrontendPlan[] {
  return SELECTABLE_PLAN_OVERLAYS.filter((overlay) =>
    !overlay.isLifetime || apiPlans.some((plan) => plan.plan_id === overlay.plan_id)
  ).map((overlay) => {
    const apiPlan = apiPlans.find((p) => p.plan_id === overlay.plan_id);
    return withDerivedDisplay({ ...overlay, ...apiPlan });
  });
}

export function isSelectablePlanId(
  planId: string | null | undefined
): planId is string {
  return SELECTABLE_PLAN_OVERLAYS.some((plan) => plan.plan_id === planId);
}

export function isResumablePlanId(
  planId: string | null | undefined
): planId is string {
  return (
    planId === "monthly_legacy" || planId === "monthly" || planId === "yearly"
  );
}

export function isLifetimePlanId(
  planId: string | null | undefined
): planId is "lifetime" {
  return planId === "lifetime";
}

export function isDirectCheckoutResumePlanId(
  planId: string | null | undefined
): planId is "monthly" | "yearly" {
  return planId === "monthly" || planId === "yearly";
}

// Summer pause is only offered for month-to-month plans. Pausing deletes the
// Mollie subscription and auto-resume re-creates it with a fresh full-price
// first charge, which is fine for a €6,99/€11 monthly plan but would re-bill a
// yearly (€84) or lifetime (€200) subscriber. A missing plan_id is
// grandfathered monthly data, so it stays pausable. Mirrors the backend gate
// in app/api/routes/subscriptions/pause.py.
export function isPausablePlanId(planId: string | null | undefined): boolean {
  return planId == null || planId === "monthly_legacy" || planId === "monthly";
}

export function getFrontendPlan(
  apiPlans: SubscriptionPlan[],
  planId: string
): FrontendPlan | undefined {
  const overlay = PLAN_LOOKUP_OVERLAYS.find((plan) => plan.plan_id === planId);
  if (!overlay) return undefined;

  const apiPlan = apiPlans.find((plan) => plan.plan_id === planId);
  return withDerivedDisplay({ ...overlay, ...apiPlan });
}

const FALLBACK_MONTHLY = FRONTEND_SUBSCRIPTION_PLANS.find(
  (p) => p.plan_id === "monthly"
)!;

export function formatPlanPriceLabel(plan: FrontendPlan | undefined): string {
  const resolved = plan ?? FALLBACK_MONTHLY;
  const amount = resolved.amount.replace(".", ",");
  if (resolved.isLifetime) return `€ ${amount} eenmalig`;
  if (resolved.interval_months === 12) return `€ ${amount} per jaar`;
  return `€ ${amount} per maand`;
}

// Compact "Bedrag" label for the account subscription card. Includes
// monthly_legacy (which is hidden from checkout but still served to existing
// subscribers) so existing customers see their real price, not the new one.
const SUBSCRIPTION_AMOUNT_LABELS: Record<string, string> = {
  monthly_legacy: "€6,99/maand",
  monthly: "€11,00/maand",
  yearly: "€84,00/jaar",
  yearly_second_child: "€60,00/jaar",
  lifetime: "€200,00 eenmalig"
};

export function formatSubscriptionAmountLabel(
  planId: string | undefined,
  apiPlans: SubscriptionPlan[] = []
): string {
  const apiPlan = planId
    ? apiPlans.find((plan) => plan.plan_id === planId)
    : undefined;
  if (apiPlan) {
    const amount = apiPlan.amount.replace(".", ",");
    if (apiPlan.plan_id === "lifetime") return `€${amount} eenmalig`;
    const interval = apiPlan.interval_months === 12 ? "jaar" : "maand";
    return `€${amount}/${interval}`;
  }

  // Fall back to monthly_legacy (€6,99) rather than monthly (€11) when the
  // plan id is missing: any account row that reaches this branch is by
  // definition pre-multi-plan data, which belonged to a grandfathered
  // €6,99 customer. Defaulting to €11 would silently raise their displayed
  // price.
  return (
    (planId && SUBSCRIPTION_AMOUNT_LABELS[planId]) ||
    SUBSCRIPTION_AMOUNT_LABELS.monthly_legacy
  );
}
